import { useCallback, useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUploadImage } from '../../../app/hooks/catalog/useUploadImage';
import cn from '../../../app/utils/cn';
import ImageUpload from './ImageUpload';

interface IImageUploadFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  disabled?: boolean;
  className?: string;
  initialImageUrl?: string;
}

type ImageUrlChange = (value: string | null) => void;

export default function ImageUploadField<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  disabled,
  className,
  initialImageUrl,
}: IImageUploadFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const { uploadAsync, progress, isUploading, abort } = useUploadImage();
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialImageUrl ?? null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setPreviewUrl(initialImageUrl ?? null);
  }, [initialImageUrl]);

  useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    },
    [],
  );

  const handleFileSelected = useCallback(
    async (file: File, onChange: ImageUrlChange) => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      const localPreview = URL.createObjectURL(file);
      blobUrlRef.current = localPreview;
      setPreviewUrl(localPreview);
      setUploadError(null);

      try {
        const { fileKey } = await uploadAsync(file);
        onChange(fileKey);
      } catch (err) {
        const message =
          err instanceof Error && err.message === 'invalid_content'
            ? t('catalog.imageUpload.errors.invalidContent')
            : t('catalog.imageUpload.errors.uploadFailed');
        setUploadError(message);
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        setPreviewUrl(initialImageUrl ?? null);
      }
    },
    [uploadAsync, t, initialImageUrl],
  );

  const handleRemove = useCallback(
    (onChange: ImageUrlChange) => {
      abort();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPreviewUrl(null);
      setUploadError(null);
      onChange(null);
    },
    [abort],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FieldBody
          value={value as string | null | undefined}
          previewUrl={previewUrl}
          setPreviewUrl={setPreviewUrl}
          blobUrlRef={blobUrlRef}
          setUploadError={setUploadError}
          initialImageUrl={initialImageUrl}
          label={label}
          className={className}
          isUploading={isUploading}
          progress={progress}
          uploadError={uploadError}
          disabled={disabled}
          error={error?.message}
          onFileSelected={(file) =>
            handleFileSelected(file, onChange as ImageUrlChange)
          }
          onRemove={() => handleRemove(onChange as ImageUrlChange)}
        />
      )}
    />
  );
}

interface IFieldBodyProps {
  value: string | null | undefined;
  previewUrl: string | null;
  setPreviewUrl: (value: string | null) => void;
  blobUrlRef: React.MutableRefObject<string | null>;
  setUploadError: (value: string | null) => void;
  initialImageUrl?: string;
  label?: string;
  className?: string;
  isUploading: boolean;
  progress: number;
  uploadError: string | null;
  disabled?: boolean;
  error?: string;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
}

function FieldBody({
  value,
  previewUrl,
  setPreviewUrl,
  blobUrlRef,
  setUploadError,
  initialImageUrl,
  label,
  className,
  isUploading,
  progress,
  uploadError,
  disabled,
  error,
  onFileSelected,
  onRemove,
}: IFieldBodyProps) {
  useEffect(() => {
    if (value === undefined) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPreviewUrl(initialImageUrl ?? null);
      setUploadError(null);
    } else if (value === null) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPreviewUrl(null);
      setUploadError(null);
    }
  }, [value, initialImageUrl, blobUrlRef, setPreviewUrl, setUploadError]);

  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      {label && (
        <span className="text-text-secondary text-sm font-medium">{label}</span>
      )}
      <ImageUpload
        value={previewUrl ?? undefined}
        onFileSelected={onFileSelected}
        onRemove={onRemove}
        isUploading={isUploading}
        progress={progress}
        error={error ?? uploadError ?? undefined}
        disabled={disabled}
      />
    </div>
  );
}
